// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEye,
    faChevronDown,
    faHouse,
    faGraduationCap,
    faDumbbell,
    faKeyboard,
    faBookOpen,
    faPlugCircleXmark,
    faEnvelope,
    faScroll,
} from '@fortawesome/free-solid-svg-icons';
import { fetchVisitCount } from '../../utils/visitTracking';
import { isDesktopLocalProfile } from '../../utils/desktopProfile';
import TurtleIcon from '../AssistantWidget/TurtleIcon';
import styles from './footer.module.scss';
import packageJson from '../../../package.json';
import { useLocalizedPath } from '../../i18n/localizedPath';
import LanguageSwitcher from '../header/LanguageSwitcher';
import JlptNotice from '../JlptNotice/JlptNotice';
import { navGroups } from '../../consts/navGroups';

// The link directory's groups. "Main pages" is footer-only (no header
// equivalent); the other four reuse the header's own navGroups catalog
// directly - see src/consts/navGroups.ts - so the footer joins the header's
// single source of truth instead of maintaining its own drifting link list.
// "Practice" drops its practiceHub entry since "Main pages" already carries a
// Practice link. A sixth group, "Site", is rendered after these (see siteLinks).
const footerGroups = [
    {
        headingKey: 'footer.nav.groupMain',
        links: [
            { to: '/', icon: faHouse, labelKey: 'footer.nav.home' },
            { to: '/learn', icon: faGraduationCap, labelKey: 'footer.nav.learn' },
            { to: '/practice', icon: faDumbbell, labelKey: 'footer.nav.practice' },
            { to: '/keyboard', icon: faKeyboard, labelKey: 'footer.nav.keyboard' },
            { to: '/vocabulary', icon: faBookOpen, labelKey: 'footer.nav.vocabulary' },
            { to: '/offline', icon: faPlugCircleXmark, labelKey: 'footer.nav.offline' },
        ],
    },
    {
        ...navGroups.groupPractice,
        links: navGroups.groupPractice.links.filter((link) => link.to !== '/practice'),
    },
    {
        ...navGroups.groupKanjiKana,
        // This route was in the header's group but missing from the old
        // flat footer entirely - use a footer-namespaced label (same text
        // as header.nav.fullKanjiList) rather than reaching into the header
        // namespace for a link the footer didn't previously have at all.
        links: navGroups.groupKanjiKana.links.map((link) =>
            link.to === '/blog/full-kanji-list' ? { ...link, labelKey: 'footer.nav.fullKanjiList' } : link
        ),
    },
    navGroups.groupTools,
    navGroups.groupResources,
];

// The last group, after footerGroups. Privacy, Cookie settings, Credits and
// Report a Bug are not here: they sit in the always-visible row above the
// directory (they must stay visible while it is folded). The language switch
// is rendered in this group as well.
const siteLinks = [
    { to: '/newsletter', icon: faEnvelope, labelKey: 'footer.nav.newsletter' },
    { to: '/releases', icon: faScroll, labelKey: 'footer.nav.releaseNotes' },
];

function openCookieSettings(event: React.MouseEvent<HTMLAnchorElement>) {
    const consentWindow = window as Window & {
        _iub?: { cs?: { api?: { openPreferences?: () => void } } };
    };
    const api = consentWindow._iub?.cs?.api;
    if (typeof api?.openPreferences === "function") {
        event.preventDefault();
        api.openPreferences();
    }
}

// One column of the directory's link grid: a real heading (so screen-reader
// users can jump to e.g. "Study tools" by heading navigation) followed by
// its links, stacked top to bottom. Children render after the links.
const FooterLinkGroup = ({ headingKey, links, children = null }) => {
    const { t } = useTranslation();
    const lp = useLocalizedPath();

    return (
        <div className={styles.footerGroup}>
            <h2 className={styles.footerGroupHeading}>{t(headingKey)}</h2>
            {links.map((link) => (
                <Link key={link.to} to={lp(link.to)} className={styles.footerLink}>
                    {link.icon && <FontAwesomeIcon icon={link.icon} className={styles.footerLinkIcon} />}
                    {t(link.labelKey)}
                </Link>
            ))}
            {children}
        </div>
    );
};

const FooterComponent = () => {
    const { t } = useTranslation();
    const lp = useLocalizedPath();
    const currentYear = new Date().getFullYear();
    const version = `v${packageJson.version}`;
    const [siteVisits, setSiteVisits] = useState(null);
    // The link directory starts collapsed on every page load and folds back
    // away on every route change (the footer stays mounted across routes, so
    // without the effect a directory opened on one page would still be open on
    // the next). The choice is deliberately not remembered.
    const [isLinksOpen, setIsLinksOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setIsLinksOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        let cancelled = false;

        fetchVisitCount()
            .then((visits) => {
                if (!cancelled && visits !== null) {
                    setSiteVisits(visits);
                }
            })
            .catch(() => {});

        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <footer className={styles.container}>
            <div className={styles.footerContent}>
                <div className={styles.topRow}>
                    <div className={styles.brandSection}>
                        <div className={styles.brandRow}>
                            <span className={styles.brandMark}><TurtleIcon size={32} animated={false} /></span>
                            <span className={styles.brand}>NihongoTech</span>
                            <span className={styles.version}>{version}</span>
                            {siteVisits !== null && (
                                <span className={styles.visitCount} title={t("footer.siteVisits")}>
                                    <FontAwesomeIcon icon={faEye} className={styles.visitCountIcon} />
                                    {siteVisits.toLocaleString()}
                                </span>
                            )}
                        </div>
                        <span className={styles.tagline}>{t("footer.tagline")}</span>
                    </div>

                    <div className={styles.linksSection}>
                    <nav className={styles.nav} aria-label={t("footer.navLabel")}>
                        {/* Always visible, even while the directory below is collapsed:
                            the privacy and licence links (2026-10-10 legal remediation)
                            and "Report a Bug" sit next to the toggle. */}
                        <div className={styles.essentialLinks}>
                            <Link to={lp("/privacy-policy")} className={styles.footerLink}>
                                {t('footer.nav.privacyPolicy')}
                            </Link>

                            {!isDesktopLocalProfile && (
                            <a
                                href="https://www.iubenda.com/privacy-policy/46335044/cookie-policy"
                                className={styles.footerLink}
                                onClick={openCookieSettings}
                            >
                                {t('footer.nav.cookieSettings')}
                            </a>
                            )}

                            <Link to={lp("/credits")} className={styles.footerLink}>
                                {t('footer.nav.credits')}
                            </Link>

                            <a
                                href="mailto:admin@teqseq.com?subject=NihongoTech%20bug%20report"
                                className={styles.footerLink}
                            >
                                {t('footer.nav.reportBug')}
                            </a>

                            <button
                                type="button"
                                className={styles.linksToggle}
                                onClick={() => setIsLinksOpen((open) => !open)}
                                aria-expanded={isLinksOpen}
                                aria-controls="footer-links"
                            >
                                {t('footer.moreLinks')}
                                <FontAwesomeIcon
                                    icon={faChevronDown}
                                    className={isLinksOpen ? `${styles.chevron} ${styles.chevronOpen}` : styles.chevron}
                                />
                            </button>
                        </div>
                        {/* Collapsed with the hidden attribute, not unmounted: the links stay
                            in the DOM for crawlers and tests, but are neither focusable nor
                            announced until the toggle opens them. */}
                        <div id="footer-links" className={styles.linksRow} hidden={!isLinksOpen}>
                            {footerGroups.map((group) => (
                                <FooterLinkGroup key={group.headingKey} headingKey={group.headingKey} links={group.links} />
                            ))}
                            <FooterLinkGroup headingKey="footer.nav.groupSite" links={siteLinks}>
                                <LanguageSwitcher variant="footer" className={styles.footerLink} />
                            </FooterLinkGroup>
                        </div>
                    </nav>
                    </div>
                </div>
                <div className={styles.metaRow}>
                    <span className={styles.madeWith}>
                        {t('footer.madeWithPrefix')}{' '}
                        <a
                            href="https://teqseq.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.brandLink}
                        >
                            {t('footer.madeWithBrand')}
                        </a>
                    </span>
                    <span className={styles.copyright}>{t('footer.copyright', { year: currentYear })}</span>
                </div>
                {/* Left-aligned, directly under the copyright line, so the signed-in
                    assistant button (fixed, bottom right) never covers these links. */}
                <p className={styles.legalNote}>
                    <Trans
                        t={t}
                        i18nKey="footer.thirdParty"
                        components={{ credits: <Link to={lp("/credits")} className={styles.legalLink} /> }}
                    />
                </p>
                <JlptNotice variant="footer" className={styles.legalNote} linkClassName={styles.legalLink} />
            </div>
        </footer>
    );
};

export default FooterComponent;
// Exported for FooterComponent.test.tsx only, to check every i18n key these
// data-driven groups ask for actually resolves in both languages - a plain
// source scan for literal t(key) calls can't see keys that only exist as data.
export { footerGroups, siteLinks };
